import { useState, type RefObject } from 'react';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { useExperiment } from '@hungryroot/experiments';
import { Heading, Paragraph } from '@hungryroot/ramen';
import { tagGroups } from '@hungryroot/tags';
import { ControlTagSelect } from '../../components/ControlTagSelect/ControlTagSelect';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { ToggleVariant, Toggle } from '../../components/Toggle/Toggle/Toggle';

import { useFilteredQuizTags } from '../../main';
import {
  type QuizPreferenceItem,
  tagAnalytics,
} from '../../segment/quizAnalytics';
import componentStyles from './Polarizing.module.css';
import styles from '../layout.module.css';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

export function Polarizing(props: Props) {
  const { testid, scrollRef } = props;
  const { getDisplayTagsFilter } = useFilteredQuizTags();
  const [trackedItems, setTrackedItems] = useState<QuizPreferenceItem[]>([]);
  useScrollToTopFixIOS(scrollRef);

  const customerDislikesVariant = useExperiment(185);

  if (customerDislikesVariant === undefined) {
    return null;
  }

  return (
    <QuizQuestionScreen
      testid={testid}
      sectionLabel={<SectionLabel label="Your tastes" />}
      subCopy="We’ll make sure you never receive them."
      heroText={
        <span>
          Do you <span className={styles.underline}>dislike</span> any of these
          foods?
        </span>
      }
      onSubmitAnalytics={() => {
        tagAnalytics('Quiz Polarizing', trackedItems);
      }}
    >
      <ControlTagSelect
        ariaLabel="Select foods you dislike"
        tagGroupId={tagGroups.polarizing2}
        expandDislikes={customerDislikesVariant > 0}
        question="dislike"
        layoutVariant="two-column"
        defaultValue={[]}
        filter={getDisplayTagsFilter('dislikes')}
        onTrackSelected={(items: QuizPreferenceItem[]) => {
          setTrackedItems(items);
        }}
        staticList
      >
        {({ active, tag, index }) => {
          const { id: tagId, name: tagName, thumbnail_url: thumbnailUrl } = tag;
          return (
            <Toggle
              active={active}
              testid={`${testid}-toggle-tag-${tagId}`}
              label={tagName}
              variant={ToggleVariant.One}
              illustration={
                thumbnailUrl && (
                  <img
                    src={thumbnailUrl}
                    alt={tagName}
                    className={componentStyles.img}
                  />
                )
              }
              style={{ animationDelay: `${0.05 * index + 0.5}s` }}
            />
          );
        }}
      </ControlTagSelect>
      <div
        className={`${componentStyles.callout} ${styles['slide-in']} ${styles['animation-delay-point-6s']}`}
      >
        <Heading variant="H6" className={componentStyles['callout-heading']}>
          Don’t see a specific food?
        </Heading>
        <Paragraph>
          No worries! You can easily mark items to never be delivered to you,
          later.
        </Paragraph>
      </div>
    </QuizQuestionScreen>
  );
}
